import cx from "classnames";
import React from "react";

import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Close, Info } from "@gaming/xds-react-js-icons";

import { useStrings } from "../../localization/localizationHelpers";
import * as styles from "./DisplayMessage.module.css";

export enum MessageType {
  SUCCESS = "SUCCESS",
  WARNING = "WARNING",
  ERROR = "ERROR",
}

type MessageProps = {
  messageType: MessageType;
  description?: string;
  content?: string;
  link?: string;
  takeActionButtonLink?: string;
  columnAligned?: boolean;
};

export const DisplayMessage = ({
  description,
  messageType,
  columnAligned,
  link,
  content,
  takeActionButtonLink,
}: MessageProps) => {
  const [warning, needMoreInformationWarningDescription, takeAction] = useStrings(
    "warning",
    "needMoreInformationWarningDescription",
    "takeAction"
  );

  const valuesByTypeMap = new Map([
    [
      MessageType.WARNING,
      {
        icon: (
          <Icon
            color="white"
            key={MessageType.WARNING}
            aria-hidden="true"
            icon={Info}
            iconSize={IconSize.Custom}
          />
        ),
        type: `${warning}`,
        defaultDescription: needMoreInformationWarningDescription,
        style: cx(styles.containerBase, styles.containerWarning),
        iconStyle: cx(styles.iconContainer, styles.iconContainerWarning),
      },
    ],
    [
      MessageType.ERROR,
      {
        icon: (
          <Icon
            color="white"
            key={MessageType.ERROR}
            aria-hidden="true"
            icon={Close}
            iconSize={IconSize.Custom}
          />
        ),
        type: "",
        defaultDescription: needMoreInformationWarningDescription,
        style: cx(styles.containerBase, styles.containerError),
        iconStyle: cx(styles.iconContainer, styles.iconContainerError),
      },
    ],
  ]);

  const authenticateLink = () => {
    if (takeActionButtonLink) {
      window.open(takeActionButtonLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className={cx(
        valuesByTypeMap.get(messageType)?.style,
        columnAligned ? styles.columnAlign : ""
      )}
    >
      <div className={styles.titleWrapper}>
        <div className={valuesByTypeMap.get(messageType)?.iconStyle}>
          {valuesByTypeMap.get(messageType)?.icon}
        </div>
        <p className={styles.title}>{valuesByTypeMap.get(messageType)?.type}</p>
      </div>
      <p className={styles.description}>
        {description ?? valuesByTypeMap.get(messageType)?.defaultDescription}
        {content ? ` ${content}` : ""}
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" className={styles.link}>
            Here
          </a>
        )}
      </p>

      {takeActionButtonLink && (
        <BasicButton
          as="button"
          buttonType={ButtonType.Primary}
          className={styles.actionButton}
          onClick={authenticateLink}
          role="button"
          textDecorationUnderline
        >
          {takeAction}
        </BasicButton>
      )}
    </div>
  );
};
